import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import {
  Autocomplete,
  Button,
  Stack,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  FormControl,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { copyJsonObject } from "../../util/commonUtils";
import {
  createDupedLine,
  clearBidAssignment,
  assignBidLine,
  assignBidGroup,
  assignDupedBidLine,
} from "../../util/bid/bidAward";

const AssignNoBidEmployeeDetailPanelContent = (props) => {
  const [assignedBidLineInput, setAssignedBidLineInput] = useState("");
  const [assignedBidLine, setAssignedBidLine] = useState("");
  const [isOverrideCheckBoxChecked, setIsOverrideCheckBoxChecked] = useState(false);
  const [bidLineAssignOptions, setBidLineAssignOptions] = useState([]);
  const [bidLineAssignErrors, setBidLineAssignErrors] = useState("");

  useEffect(() => {
    let bidLineValue = "";
    let isNoBidOverridden = false;
    if (props.employee.awardedLine) {
      bidLineValue = props.employee.awardedLine.toString().split(".")[0].toString();
      isNoBidOverridden = props.employee.isNoBidOverridden ?? false;
    }
    setAssignedBidLine(bidLineValue);
    setAssignedBidLineInput(bidLineValue);
    setIsOverrideCheckBoxChecked(isNoBidOverridden);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOverrideCheckBoxChecked) {
      setBidLineAssignOptions(getBidLineListToOverride().map((line) => line.lineNumber.toString()));
    } else {
      setBidLineAssignOptions(getUnawardedBidLines().map((line) => line.lineNumber.toString()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.bidLines, isOverrideCheckBoxChecked]);

  const getUnawardedBidLines = () => {
    let unawardedBidLines = props.bidLines.filter(
      (line) =>
        (!line.awardedEid && line.ftpt === props.employee.ftpt) || line.lineNumber.toString() === assignedBidLine
    );
    return unawardedBidLines;
  };

  const getBidLineListToOverride = () => {
    let validBidLines = props.bidLines.filter(
      (line) => line.ftpt === props.employee.ftpt && line.awardedEid && line.awardType !== "BL" && !line.isDupedLine
    );
    return validBidLines;
  };

  const handleBidLineAssignChange = (event, value, reason) => {
    if (!props.isIAOOverwriteCheckRequired()) {
      if (reason === "clear") {
        const clearedBidLinesResult = clearBidAssignment(props.bidLines, props.bidGroup, props.employee.eid);
        props.setBidLines(clearedBidLinesResult.bidLines);
        props.setBidGroup(clearedBidLinesResult.bidGroup);
      }
      if (assignedBidLine !== value) {
        props.setIsNoBidListChanged(true);
      }
      setBidLineAssignErrors("");
      setAssignedBidLine(value);
    }
  };

  const isValidLineToOverride = () => {
    let awardedBidLineIdx = props.bidLines.findIndex(
      (line) => line.awardedEid && line.ftpt === props.employee.ftpt && line.lineNumber.toString() === assignedBidLine
    );
    if (awardedBidLineIdx !== -1) {
      setBidLineAssignErrors("");
      return true;
    }
    setBidLineAssignErrors("Invalid Bid Line to Override");
    return false;
  };

  const isValidLineToAssign = () => {
    let awardedBidLineIdx = props.bidLines.findIndex(
      (line) => line.awardedEid && line.ftpt === props.employee.ftpt && line.lineNumber.toString() === assignedBidLine
    );
    if (awardedBidLineIdx !== -1) {
      setBidLineAssignErrors("Bid line already assigned");
      return false;
    }
    setBidLineAssignErrors("");
    return true;
  };

  const handleDuplicateAssignment = () => {
    if (assignedBidLine && isValidLineToOverride()) {
      let dupedLineObj = null;

      // Assign to bidLines
      const selectedBidLineIdx = props.bidLines.findIndex(
        (bidLine) => bidLine?.lineNumber.toString() === assignedBidLine
      );
      if (selectedBidLineIdx !== -1) {
        let copiedBidLines = copyJsonObject(props.bidLines);
        dupedLineObj = createDupedLine(copiedBidLines, copiedBidLines[selectedBidLineIdx], true);
        copiedBidLines = assignDupedBidLine(copiedBidLines, selectedBidLineIdx, dupedLineObj, props.employee);
        props.setBidLines(copiedBidLines);
      }

      // Assign to bidGroup
      const selectedBidGroupIdx = props.bidGroup.findIndex((emp) => emp?.eid === props.employee.eid);
      if (selectedBidGroupIdx !== -1) {
        const updatedBidGroup = assignBidGroup(
          props.bidGroup,
          selectedBidGroupIdx,
          dupedLineObj.dupedLine.lineNumber,
          props.employee.awardType,
          isOverrideCheckBoxChecked
        );
        props.setBidGroup(updatedBidGroup);
      }
    }
  };

  const handleAssign = () => {
    if (isOverrideCheckBoxChecked) {
      handleDuplicateAssignment();
    } else if (assignedBidLine && isValidLineToAssign()) {
      // Clear existing assignments
      const clearedBidLinesResult = clearBidAssignment(props.bidLines, props.bidGroup, props.employee.eid);
      // Assign to bidLines
      const selectedBidLineId = clearedBidLinesResult.bidLines.findIndex(
        (bidLine) => bidLine?.lineNumber.toString() === assignedBidLine
      );
      if (selectedBidLineId !== -1) {
        const updatedBidLines = assignBidLine(clearedBidLinesResult.bidLines, selectedBidLineId, props.employee);
        props.setBidLines(updatedBidLines);
      }

      // Assign to bidGroup
      const selectedBidGroupIdx = clearedBidLinesResult.bidGroup.findIndex((emp) => emp?.eid === props.employee.eid);
      if (selectedBidGroupIdx !== -1) {
        const updatedBidGroup = assignBidGroup(
          clearedBidLinesResult.bidGroup,
          selectedBidGroupIdx,
          parseFloat(assignedBidLine),
          props.employee.awardType
        );
        props.setBidGroup(updatedBidGroup);
      }
    }
  };

  const handleOverrideCheckBoxClicked = (event) => {
    setIsOverrideCheckBoxChecked(event.target.checked);
    setAssignedBidLine("");
    setBidLineAssignErrors("");
  };

  return (
    <Stack
      direction="row"
      sx={{
        display: "flex",
        paddingLeft: "1rem",
        paddingTop: "10px",
        paddingBottom: "10px",
        alignItems: "center",
        backgroundColor: "secondary.swaLightestBlue",
      }}
    >
      <Typography variant="subtitle1">Assign Bid Line</Typography>
      <Autocomplete
        id="bid-line-number"
        label="Bid Line #"
        options={bidLineAssignOptions}
        isOptionEqualToValue={(option, value) => option === value || value === ""}
        value={assignedBidLine}
        onChange={handleBidLineAssignChange}
        inputValue={assignedBidLineInput}
        onInputChange={(event, newInputValue) => {
          setAssignedBidLineInput(newInputValue);
        }}
        sx={{ marginLeft: "1rem", width: "160px" }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label="Bid Line #"
            size="small"
            sx={{ backgroundColor: "secondary.swaWhite", borderRadius: "5px" }}
            error={bidLineAssignErrors !== ""}
            helperText={bidLineAssignErrors}
          />
        )}
      />
      <Button
        variant="contained"
        color="primary"
        startIcon={<AssignmentIndIcon />}
        component="label"
        onClick={handleAssign}
        disabled={assignedBidLine === "" || assignedBidLine === null}
        sx={{ marginLeft: "1rem", height: "40px" }}
      >
        Assign
      </Button>
      <Tooltip title="Duplicate a line already awarded to an Employee in the NO BID section." placement="bottom" arrow>
        <span>
          <FormControl required component="fieldset" sx={{ marginLeft: "1rem" }}>
            <FormControlLabel
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "13px",
                  marginRight: "5px",
                },
              }}
              control={
                <Checkbox
                  checked={isOverrideCheckBoxChecked}
                  onChange={(event) => handleOverrideCheckBoxClicked(event)}
                  disabled={props.employee.awardedLine && assignedBidLine !== ""}
                />
              }
              label="OVERRIDE"
            />
          </FormControl>
        </span>
      </Tooltip>
    </Stack>
  );
};

AssignNoBidEmployeeDetailPanelContent.propTypes = {
  employee: PropTypes.object.isRequired,
  bidLines: PropTypes.array.isRequired,
  setBidLines: PropTypes.func.isRequired,
  noBidList: PropTypes.array.isRequired,
  bidGroup: PropTypes.array.isRequired,
  setBidGroup: PropTypes.func.isRequired,
  setIsNoBidListChanged: PropTypes.func.isRequired,
  isIAOOverwriteCheckRequired: PropTypes.func.isRequired,
};

export default AssignNoBidEmployeeDetailPanelContent;
